/**
 * Success UI for BK final STL finalize (credits deducted server-side).
 * Does not call deduct APIs — only interprets finalize payload for toast.
 */

import { toast } from 'react-toastify';

/**
 * @param {unknown} raw
 * @returns {number|null}
 */
function parsePositiveAmount(raw) {
  const n =
    typeof raw === 'number' ? raw : typeof raw === 'string' ? Number(raw) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Prefer `credits_deducted`, fall back to `custom_benx_credits`.
 * @param {Record<string, unknown>} payload
 * @returns {number|null}
 */
function resolveCreditsAmount(payload) {
  return (
    parsePositiveAmount(payload.credits_deducted) ??
    parsePositiveAmount(payload.custom_benx_credits)
  );
}

/**
 * @param {unknown} result - Already-unwrapped Frappe `message` from finalize/uploadFinalStl
 * @returns {Record<string, unknown>|null}
 */
export function unwrapBkFinalizePayload(result) {
  if (!result || typeof result !== 'object') return null;
  // Defensive: some callers may pass the full `{ message: {...} }` envelope.
  const maybeEnvelope = /** @type {Record<string, unknown>} */ (result);
  if (
    maybeEnvelope.message &&
    typeof maybeEnvelope.message === 'object' &&
    !Array.isArray(maybeEnvelope.message) &&
    ('credits_deducted' in /** @type {object} */ (maybeEnvelope.message) ||
      'custom_benx_credits_applied' in /** @type {object} */ (maybeEnvelope.message) ||
      'status' in /** @type {object} */ (maybeEnvelope.message))
  ) {
    return /** @type {Record<string, unknown>} */ (maybeEnvelope.message);
  }
  return maybeEnvelope;
}

/**
 * Newly deducted credits toast — skip idempotent replays and zero-credit exports.
 * @param {Record<string, unknown>|null|undefined} payload
 */
export function shouldShowBkCreditsDeductedToast(payload) {
  if (!payload) return false;
  if (payload.idempotent === true) return false;
  const applied = payload.custom_benx_credits_applied === true;
  const deducted = parsePositiveAmount(payload.credits_deducted);
  return applied || deducted != null;
}

/**
 * @param {Record<string, unknown>} payload
 */
export function formatBkCreditsDeductedToastMessage(payload) {
  const amount = resolveCreditsAmount(payload);
  if (amount == null) {
    return 'Export completed successfully.';
  }
  const creditLabel = amount === 1 ? 'BenX Credit' : 'BenX Credits';
  return `Export completed. ${amount} ${creditLabel} used.`;
}

/**
 * Soft success toast when no newly deducted credits (zero-credit / idempotent).
 * @param {Record<string, unknown>|null|undefined} payload
 */
function formatExportSuccessToast(payload) {
  if (payload?.idempotent === true) {
    if (typeof payload?.message === 'string' && payload.message.trim()) {
      return payload.message.trim();
    }
    return 'Export already finalized.';
  }
  if (typeof payload?.message === 'string' && payload.message.trim()) {
    return payload.message.trim();
  }
  const version = payload?.version;
  return version
    ? `Final STL exported (v${version}).`
    : 'Export completed successfully.';
}

/**
 * Show Finish success toast from finalize payload.
 * @param {unknown} finalizeResult
 */
export function notifyBkFinalExportSuccessUi(finalizeResult) {
  const payload = unwrapBkFinalizePayload(finalizeResult);
  const showCredits = shouldShowBkCreditsDeductedToast(payload);

  if (showCredits) {
    toast.success(
      formatBkCreditsDeductedToastMessage(/** @type {Record<string, unknown>} */ (payload)),
    );
  } else {
    toast.success(formatExportSuccessToast(payload));
  }
}
